/*
  cfi.js — minimal progressive enhancement for the CFII site.
  Loaded with `defer` from layout/theme.liquid. Everything below is optional:
  the page is fully usable (including all anchor navigation) with JS disabled.

  Responsibilities:
    1. Mobile nav open/close with correct ARIA state.
    2. Shadow on the sticky header once the page is scrolled.
    3. Scroll-spy that marks the nav link for the section currently in view.
*/
(function () {
  'use strict';

  // The sticky element is the section wrapper itself (schema uses tag: header,
  // class: cfi-header) — sticky only works when it is a direct child of <body>.
  var header = document.querySelector('.cfi-header');
  if (!header) return;

  /* --- 1. Mobile navigation ------------------------------------ */

  var toggle = header.querySelector('[data-cfi-toggle]');
  var panel = header.querySelector('[data-cfi-mobile-nav]');

  function closeMenu() {
    if (!toggle || !panel) return;
    toggle.setAttribute('aria-expanded', 'false');
    panel.hidden = true;
  }

  function openMenu() {
    if (!toggle || !panel) return;
    toggle.setAttribute('aria-expanded', 'true');
    panel.hidden = false;
  }

  if (toggle && panel) {
    toggle.addEventListener('click', function () {
      if (toggle.getAttribute('aria-expanded') === 'true') {
        closeMenu();
      } else {
        openMenu();
      }
    });

    // Tapping a link should navigate and dismiss the panel.
    panel.addEventListener('click', function (event) {
      if (event.target.closest('a')) closeMenu();
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && toggle.getAttribute('aria-expanded') === 'true') {
        closeMenu();
        toggle.focus();
      }
    });

    // If the viewport grows past the desktop breakpoint, reset to a clean state.
    var desktop = window.matchMedia('(min-width: 990px)');
    var onBreakpoint = function (event) {
      if (event.matches) closeMenu();
    };
    if (typeof desktop.addEventListener === 'function') {
      desktop.addEventListener('change', onBreakpoint);
    }
  }

  /* --- 2. Header shadow on scroll ------------------------------ */

  var ticking = false;
  function updateScrollState() {
    header.dataset.scrolled = window.scrollY > 8 ? 'true' : 'false';
    ticking = false;
  }
  window.addEventListener(
    'scroll',
    function () {
      if (!ticking) {
        window.requestAnimationFrame(updateScrollState);
        ticking = true;
      }
    },
    { passive: true }
  );
  updateScrollState();

  /* --- 3. Scroll-spy ------------------------------------------- */

  if (!('IntersectionObserver' in window)) return;

  var links = Array.prototype.slice.call(header.querySelectorAll('a[href*="#"]'));
  var byId = {};
  var targets = [];

  links.forEach(function (link) {
    var hash = link.getAttribute('href').split('#')[1];
    if (!hash) return;
    var target = document.getElementById(hash);
    if (!target) return;
    if (!byId[hash]) {
      byId[hash] = [];
      targets.push(target);
    }
    byId[hash].push(link);
  });

  if (!targets.length) return;

  var current = null;

  function setCurrent(id) {
    if (id === current) return;
    if (current && byId[current]) {
      byId[current].forEach(function (link) {
        link.removeAttribute('aria-current');
      });
    }
    current = id;
    if (current && byId[current]) {
      byId[current].forEach(function (link) {
        link.setAttribute('aria-current', 'true');
      });
    }
  }

  // The anchors are zero-height markers at the top of each section. Treat the
  // band just below the header as the "active" line: the last anchor to have
  // crossed it is the section the reader is looking at.
  var seen = {};

  var observer = new IntersectionObserver(
    function (entries) {
      entries.forEach(function (entry) {
        seen[entry.target.id] = entry.boundingClientRect.top < 0 || entry.isIntersecting;
      });

      var active = null;
      targets.forEach(function (target) {
        if (seen[target.id]) active = target.id;
      });
      setCurrent(active);
    },
    { rootMargin: '-20% 0px -75% 0px', threshold: 0 }
  );

  targets.forEach(function (target) {
    observer.observe(target);
  });
})();
